import Alert from '@storeblocks/alert';
import Button from '@storeblocks/button';
import Input from '@storeblocks/input/lib';
import { add, format } from 'date-fns';
import { Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import {
  InsuranceDetails,
  postAutomaticInsuranceCancellation,
} from 'Api/insurance';
import { validateAccountNumberMod11 } from 'Pages/CancelInsurancePage/cancelInsurancePage.helpers';
import {
  AlertContainer,
  ButtonContainer,
  FieldsWrapper,
} from 'Pages/CancelInsurancePage/cancelInsurancePage.styled';
import { getAppText } from 'Utils/text';
import { CancellationReason } from './cancelForm.constants';

type FormValues = {
  bankAccount: string | null;
};

type VehicleCancellationFormProps = {
  insuranceDetails: InsuranceDetails;
  cancellingLastInsurance: boolean;
};

export const VehicleCancellationForm = ({
  insuranceDetails,
  cancellingLastInsurance,
}: VehicleCancellationFormProps) => {
  const [submitError, setSubmitError] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const navigate = useNavigate();
  const { policyNumber, organizationPillar, insuranceProduct } =
    insuranceDetails;

  const hasMortgagee: boolean =
    'hasMortgagee' in insuranceDetails && !!insuranceDetails.hasMortgagee;

  return (
    <>
      {submitSuccess && (
        <AlertContainer>
          <Alert
            variant="success"
            title={getAppText('cancelInsurance.alert.submitSuccess.title')}
            description={getAppText(
              'cancelInsurance.alert.submitSuccess.content',
            )}
          />
        </AlertContainer>
      )}
      {submitError && (
        <AlertContainer>
          <Alert
            variant="error"
            title={getAppText('cancelInsurance.alert.submitError')}
          />
        </AlertContainer>
      )}

      <AlertContainer>
        {hasMortgagee ? (
          <Alert title={getAppText('cancelInsurance.info.hasMortgageeInfo')} />
        ) : (
          <Alert title={getAppText('cancelInsurance.info.deregisteredAuto')} />
        )}
      </AlertContainer>

      <Formik
        initialValues={{
          bankAccount: null,
        }}
        validationSchema={yup.object({
          bankAccount: cancellingLastInsurance
            ? yup
                .string()
                .nullable()
                .test(
                  'isValidAccountNumber',
                  'Ugyldig kontonummer',
                  validateAccountNumberMod11,
                )
                .required(
                  getAppText('cancelInsurance.bankAccount.error.required'),
                )
            : yup.string().nullable(),
        })}
        onSubmit={async ({ bankAccount }: FormValues) => {
          const values = {
            cancelReason: CancellationReason.OBJECT_AT_RISK_NOT_EXIST_ANY_MORE,
            cancelDate: format(add(new Date(), { days: 30 }), 'dd.MM.yyyy'),
            bankAccount,
          };

          try {
            setSubmitError(false);

            await postAutomaticInsuranceCancellation(
              organizationPillar,
              policyNumber,
              values,
            );

            setSubmitSuccess(true);
          } catch {
            setSubmitError(true);
          }
        }}
      >
        {({ touched, errors, isValid, isSubmitting, submitCount }) => (
          <Form>
            <FieldsWrapper>
              {cancellingLastInsurance && (
                <Field
                  className="data-hj-suppress"
                  as={Input}
                  label={getAppText('cancelInsurance.bankAccount.label')}
                  hint={getAppText('cancelInsurance.bankAccount.description')}
                  name="bankAccount"
                  error={touched.bankAccount && errors.bankAccount}
                />
              )}

              <ButtonContainer>
                {!submitSuccess && (
                  <Button
                    type="submit"
                    data-trackid={`button-send_cancellation_insurance_${insuranceProduct}`}
                    loading={isSubmitting}
                    disabled={(submitCount > 0 && !isValid) || hasMortgagee}
                  >
                    {getAppText('cancelInsurance.submit.button.label')}
                  </Button>
                )}
                <Button
                  data-trackid={`button-go_back_cancel_insurance_${insuranceProduct}`}
                  iconLeft="arrow-left"
                  variant="text"
                  onClick={() => navigate(-1)}
                >
                  {getAppText('cancelInsurance.button.goBack')}
                </Button>
              </ButtonContainer>
            </FieldsWrapper>
          </Form>
        )}
      </Formik>
    </>
  );
};
